import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Select,
  Upload,
  message,
  Button,
  Modal,
  DatePicker,
  Spin,
} from "antd";
import { useForm } from "antd/es/form/Form";
import DocumentBox from "../../../components/common/DocumentBox";
import { EditFilled, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import API_MANAGER from "../../../API";
import { s3PreUrl } from "../../../constants";
import axios from "axios";

function PoliceClearanceEdit({
  data,
  policeClearanceEdit,
  setPoliceClearanceEdit,
  certifyingAuthority,
  onUpdate,
}) {
  const [policeForm] = useForm();
  const [editDocument, setEditDocument] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentDeleted, setDocumentDeleted] = useState(false);

  const props = {
    name: "file",
    accept: "image/jpg,image/jpeg,.pdf",
    multiple: false,
    customRequest({ onSuccess }) {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    onChange(info) {
      const { status } = info.file;
      if (status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const handleUpload = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    setDocumentDeleted(false); // Reset delete flag if new file is uploaded
  };

  const handleDeleteDocument = () => {
    Modal.confirm({
      title: "Delete Document",
      content: "Are you sure you want to delete this document?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        setDocumentDeleted(true);
        setEditDocument(false);
        setFileList([]);
        message.success("Document marked for deletion. Click Update to save changes.");
      },
    });
  };

  const handleFinish = async (values) => {
    console.log("values: ", values);
    setLoading(true);
    try {
      // Check if document is required (for new records)
      const isNewRecord = !data?.policeClearanceDetails?.document;
      if (isNewRecord && (!fileList || fileList.length === 0 || !fileList[0]?.originFileObj)) {
        message.error("Please upload a document");
        setLoading(false);
        return;
      }

      let documentUrl = data?.policeClearanceDetails?.document || "";

      // Handle document deletion
      if (documentDeleted) {
        documentUrl = ""; // Set empty string to delete document
      } else if (fileList?.length > 0 && fileList[0]?.originFileObj) {
        // Handle file upload if a new document is selected
        const dataDocument = {
          extension: fileList[0]?.type.split("/")[1],
          type: "DOCUMENT",
          name: fileList[0].originFileObj.name,
          contentType: fileList[0]?.type,
        };
        const responseDocument = await API_MANAGER.fileUpload(dataDocument);
        await axios.put(
          responseDocument?.data?.data?.url,
          fileList[0]?.originFileObj
        );
        documentUrl = s3PreUrl + responseDocument.data?.data?.key;
      }

      // Format issue date - for new records, issueDate must come from form
      const issueDate = values.issueDate 
        ? dayjs(values.issueDate).toISOString() 
        : (data?.policeClearanceDetails?.issueDate || null);

      const payload = {
        userId: data?.userId,
        hasPoliceClearance: true,
        policeClearanceDetails: {
          document: documentUrl,
          certifyingAuthority: values.certifyingAuthority,
          issueDate: issueDate,
        },
      };

      console.log("Updating police clearance with payload:", payload);
      await API_MANAGER.editCandidateAdditionalDetails(payload);
      message.success(data?.policeClearanceDetails?.document ? "Successfully updated police clearance" : "Successfully added police clearance");
      setLoading(false);
      setPoliceClearanceEdit(false);
      setDocumentDeleted(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error updating police clearance:", error);
      message.error(
        error?.response?.data?.message?.message || "Error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const setFields = () => {
    policeForm.setFieldsValue({
      certifyingAuthority:
        data?.policeClearanceDetails?.certifyingAuthority?._id ||
        data?.policeClearanceDetails?.certifyingAuthority?.id,
      issueDate: data?.policeClearanceDetails?.issueDate
        ? dayjs(data.policeClearanceDetails.issueDate)
        : null,
    });
  };

  useEffect(() => {
    setFields();
    setDocumentDeleted(false); // Reset delete state when data changes
    setEditDocument(false); // Reset edit state
    setFileList([]); // Reset file list
  }, [data]);

  return (
    <Modal
      centered
      footer={false}
      open={policeClearanceEdit}
      title={data?.policeClearanceDetails?.document ? "Edit Police Clearance" : "Add Police Clearance"}
      className="policeClearanceModal"
      onCancel={() => {
        setPoliceClearanceEdit(false);
        setDocumentDeleted(false);
        setEditDocument(false);
        setFileList([]);
      }}
      width={800}
    >
      <Form form={policeForm} layout="vertical" onFinish={handleFinish}>
        <Spin spinning={loading} />
        <Row className="containner">
          <Col xs={18}>
            {editDocument || documentDeleted || !data?.policeClearanceDetails?.document ? (
              <Upload {...props} onChange={handleUpload}>
                <Button icon={<UploadOutlined />}>Upload {documentDeleted ? "New" : ""} Document</Button>
              </Upload>
            ) : (
              <DocumentBox
                date={data?.policeClearanceDetails?.issueDate}
                url={data?.policeClearanceDetails?.document}
              />
            )}
            {documentDeleted && (
              <div style={{ padding: "10px 0", color: "#ff4d4f", fontSize: "12px" }}>
                <p>Previous document will be deleted. Upload a new document or click Update to confirm deletion.</p>
              </div>
            )}
          </Col>
          <Col className="editResume" xs={6}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {!editDocument && !documentDeleted && data?.policeClearanceDetails?.document && (
                <>
                  <DeleteOutlined 
                    onClick={handleDeleteDocument}
                    style={{ 
                      color: "#ff4d4f", 
                      fontSize: "18px", 
                      cursor: "pointer" 
                    }}
                    title="Delete Document"
                  />
                  <EditFilled 
                    onClick={() => setEditDocument(true)}
                    style={{ fontSize: "18px", cursor: "pointer" }}
                  />
                </>
              )}
            </div>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Certifying Authority*"
              name="certifyingAuthority"
              rules={[
                {
                  required: true,
                  message: "Please select certifying authority",
                },
              ]}
            >
              <Select
                placeholder="Select certifying authority"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {certifyingAuthority?.map((auth, key) => (
                  <Select.Option key={key} value={auth.id || auth._id}>
                    {auth.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Issue Date*"
              name="issueDate"
              rules={[
                {
                  required: true,
                  message: "Please select issue date",
                },
              ]}
            >
              <DatePicker
                className="w-100"
                format={"DD-MM-YYYY"}
                placeholder="Select issue date"
                style={{ height: "38px" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col xs={24} className="buttonCol">
            <Button className="btn1" htmlType="submit" loading={loading}>
              {data?.policeClearanceDetails?.document ? "Update" : "Create"}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default PoliceClearanceEdit;

